import Vue from "vue";
import Vuex from "vuex";
import _ from "underscore";
import geolocation from "./geolocation.js";
import imageFlipper from "./image-flipper";
import inset from "./inset.js";
import rides from "./rides";
import tours from "./tours.js";
import ui from "./ui.js";
import syncQueryParams from "./syncQueryParams";
import queryString from "query-string";
import seedrandom from "seedrandom";
import { excludedTags } from "../constants/constants.js";

Vue.use(Vuex);

const query = queryString.parse(window.location.href.split("?")[1] || "");
const dataDir = `${process.env.VUE_APP_STATIC_PATH}/data`;

export default new Vuex.Store({
  plugins: [syncQueryParams],
  modules: {
    geolocation,
    imageFlipper,
    inset,
    rides,
    tours,
    ui
  },
  state: {
    data: {
      "1965": [],
      "1966": [],
      "1973": [],
      "1974": [],
      "1975": [],
      "1976": [],
      "1985": [],
      "1990": [],
      "1995": [],
      "1997": [],
      "1998": [],
      "2007": []
    },
    years: [
      "1965",
      "1966",
      "1973",
      "1974",
      "1975",
      "1976",
      "1985",
      "1990",
      "1995",
      "1997",
      "1998",
      "2007"
    ],
    ocr: [],
    popularOcr: [],
    filters: query.filters
      ? (Array.isArray(query.filters) ? query.filters : [query.filters]).map(
          f => {
            let s = decodeURIComponent(f).split(",");
            let filter = { category: s[0] };
            let value = s[1];
            if (value[0] === "~") {
              value = value.slice(1);
              filter.invert = true;
            }
            filter.value = value;
            return filter;
          }
        )
      : [],
    yearOverride: false,
    seed: query.seed ? query.seed : false,
    isMobile: window.innerWidth < 815,
    isLandscape: window.innerWidth > window.innerHeight,
    anyImagesLoaded: false,
    loadedImages: []
  },
  getters: {
    allImages(state) {
      return state.years.reduce(
        (acc, cur) => [
          ...acc,
          ...state.data[cur].map(f => ({
            ...f,
            year: cur
          }))
        ],
        []
      );
    },

    numImagesTotal(state, getters) {
      return getters.allImages.length;
    },

    filteredImages(state, getters) {
      let filteredImages = getters.allImages.filter(i => {
        let matches = true;
        state.filters.forEach(filter => {
          if (filter.category === "year") {
            if (filter.invert) {
              if (filter.value === i.year) matches = false;
            } else {
              if (filter.value !== i.year) matches = false;
            }
          }

          if (filter.category === "tag") {
            if (filter.invert) {
              if (i.tags.map(t => t.toLowerCase()).indexOf(filter.value) >= 0) {
                matches = false;
              }
            } else {
              if (i.tags.map(t => t.toLowerCase()).indexOf(filter.value) < 0) {
                matches = false;
              }
            }
          }

          if (filter.category === "ocr") {
            if (filter.invert) {
              if (
                i.ocr
                  .map(t => t.toLowerCase())
                  .indexOf(filter.value.toLowerCase()) >= 0
              ) {
                matches = false;
              }
            } else {
              if (
                i.ocr
                  .map(t => t.toLowerCase())
                  .indexOf(filter.value.toLowerCase()) < 0
              ) {
                matches = false;
              }
            }
          }
        });

        return matches;
      });

      return filteredImages;
    },

    numImagesFiltered(state, getters) {
      return getters.filteredImages.length;
    },

    shuffledFilteredImages(state, getters) {
      let rng = seedrandom(state.seed);

      function shuffle(array) {
        var m = array.length,
          t,
          i;

        // While there remain elements to shuffle…
        while (m) {
          // Pick a remaining element…
          i = Math.floor(rng() * m--);

          // And swap it with the current element.
          t = array[m];
          array[m] = array[i];
          array[i] = t;
        }

        return array;
      }

      return shuffle(getters.filteredImages.slice());
    },

    mappedImages(state) {
      let images = {};

      state.years.forEach(y => {
        let mapped = state.data[y]
          .map(p => ({
            ocr: p.ocr,
            location: p.location,
            id: p.id,
            tags: p.tags,
            north: p.north
          }))
          .sort((a, b) => b.location - a.location);

        let north = mapped.filter(p => p.north);
        let south = mapped.filter(p => !p.north).reverse();

        images[y] = { north, south };
      });

      return images;
    },

    flattenedMappedImages(state, getters) {
      let images = [];

      state.years.forEach(y => {
        images.push(
          ...getters.mappedImages[y].north.map(i => ({
            ...i,
            north: true,
            year: y
          }))
        );
        images.push(
          ...getters.mappedImages[y].south.map(i => ({
            ...i,
            north: false,
            year: y
          }))
        );
      });

      return images;
    },

    tagCounts(_, getters) {
      let count = {};

      getters.flattenedMappedImages.forEach(im => {
        im.tags.forEach(t => {
          if (!count.hasOwnProperty(t.toLowerCase())) {
            count[t.toLowerCase()] = 1;
          } else {
            count[t.toLowerCase()] += 1;
          }
        });
      });

      return count;
    },

    ocrCounts(_, getters) {
      let count = {};

      getters.flattenedMappedImages.forEach(im => {
        im.ocr.forEach(t => {
          if (!count.hasOwnProperty(t.toLowerCase())) {
            count[t.toLowerCase()] = 1;
          } else {
            count[t.toLowerCase()] += 1;
          }
        });
      });

      return count;
    },

    yearCounts(state, getters) {
      let count = {};

      state.years.forEach(y => {
        count[y] = 0;
      });

      getters.flattenedMappedImages.forEach(im => {
        count[im["year"]] += 1;
      });

      return count;
    },

    tags(state) {
      let all_tags = _.countBy(
        _.flatten(
          state.years.map(y =>
            state.data[y].map(f => f.tags.map(t => t.toLowerCase()))
          )
        ).filter(t => excludedTags.indexOf(t) < 0),
        x => x
      );

      let tag_list = Object.keys(all_tags)
        .filter(t => all_tags[t] >= 3)
        .sort((a, b) => all_tags[b] - all_tags[a]);

      return tag_list;
    },

    popularTags(state) {
      let all_tags = _.countBy(
        _.flatten(
          state.years.map(y =>
            state.data[y].map(f => f.tags.map(t => t.toLowerCase()))
          )
        ),
        x => x
      );

      let tag_list = Object.keys(all_tags)
        .filter(t => all_tags[t] >= 3)
        .sort((a, b) => all_tags[b] - all_tags[a]);
      let popular_tags = tag_list.filter(o => all_tags[o] > 250);

      return popular_tags;
    },

    ocr(state) {
      let all_ocr = _.countBy(
        _.flatten(
          state.years.map(y =>
            state.data[y].map(f =>
              f.ocr.filter(t => t.length >= 3).map(t => t.toLowerCase())
            )
          )
        ),
        x => x
      );

      let ocr_list = Object.keys(all_ocr).sort(
        (a, b) => all_ocr[b] - all_ocr[a]
      );

      return ocr_list;
    },

    popularOcr(state) {
      let all_ocr = _.countBy(
        _.flatten(
          state.years.map(y =>
            state.data[y].map(f =>
              f.ocr.filter(t => t.length >= 3).map(t => t.toLowerCase())
            )
          )
        ),
        x => x
      );

      let ocr_list = Object.keys(all_ocr).sort(
        (a, b) => all_ocr[b] - all_ocr[a]
      );

      let popular_ocr = ocr_list.filter(o => all_ocr[o] > 100);

      return popular_ocr;
    }
  },
  mutations: {
    setData(state, { data }) {
      state.data = Object.freeze({ ...state.data, ...data });
    },

    setImages(state, { images }) {
      if (!state.images) state.images = {};
      state.images = { ...state.images, ...images };
    },

    setFilters(state, { filters }) {
      state.filters = filters;
    },

    setYearOverride(state, year) {
      state.yearOverride = year;
    },

    setSeed(state, { seed }) {
      state.seed = seed;
    },

    setIsMobile(state, mobile) {
      state.isMobile = mobile;
    },

    setIsLandscape(state, landscape) {
      state.isLandscape = landscape;
    },

    addLoadedImage(state, imageId) {
      state.loadedImages.push(imageId);
    }
  },
  actions: {
    loadImageData({ commit }) {
      let parseData = data => {
        let images = {};
        Object.keys(data).forEach(y =>
          data[y].forEach(f => {
            let id = f.id;
            images[id] = {
              image: null,
              startedLoad: false
            };
          })
        );

        commit("setData", { data });
        commit("setImages", { images });
      };

      fetch(`${dataDir}/1966.json`)
        .then(r => r.json())
        .then(parseData)
        .then(() =>
          Promise.all([
            fetch(`${dataDir}/1985.json`).then(r => r.json()),
            fetch(`${dataDir}/2007.json`).then(r => r.json())
          ])
        )
        .then(r => {
          r.map(y => parseData(y));
        })
        .then(() =>
          Promise.all([
            fetch(`${dataDir}/1965.json`).then(r => r.json()),
            fetch(`${dataDir}/1973.json`).then(r => r.json()),
            fetch(`${dataDir}/1974.json`).then(r => r.json()),
            fetch(`${dataDir}/1975.json`).then(r => r.json()),
            fetch(`${dataDir}/1976.json`).then(r => r.json()),
            fetch(`${dataDir}/1990.json`).then(r => r.json()),
            fetch(`${dataDir}/1995.json`).then(r => r.json()),
            fetch(`${dataDir}/1997.json`).then(r => r.json()),
            fetch(`${dataDir}/1998.json`).then(r => r.json())
          ])
        )
        .then(r => {
          r.map(y => parseData(y));
        });
    },

    removeOlderImages({ state }) {
      const threshold = state.isMobile ? 500 : 1500;
      if (state.loadedImages.length > threshold) {
        const toRemove = state.loadedImages.slice(0, 100);
        state.loadedImages = state.loadedImages.slice(100);
        toRemove.forEach(id => (state.images[id] = {}));
      }
    },

    loadImage({ commit, dispatch, state }, id) {
      dispatch("removeOlderImages");
      state.images[id].startedLoad = true;
      state.images[id].loaded = false;
      state.images[id].image = new Image();
      state.images[id].image.src =
        "https://media.getty.edu/iiif/image/" + id + "/full/512,/0/default.jpg";
      state.images[id].image.crossOrigin = "anonymous";

      state.images[id].image.onload = () => {
        commit("addLoadedImage", id);
        state.images[id].loaded = true;
        state.anyImagesLoaded = true;
      };

      state.images[id].image.onerror = () => {
        state.images[id].startedLoad = false;
        state.images[id].loaded = false;
      };
    },

    cancelLoadImage({ state }, id) {
      if (state.images[id].startedLoad && !state.images[id].loaded) {
        state.images[id].image.src = "";
        state.images[id].startedLoad = false;
      }
    },

    setRandomFilter({ commit, state, getters, dispatch }) {
      let r = Math.random();
      let filters = [];

      if (r > 0.6) {
        filters.push({
          category: "year",
          value: _.sample(state.years)
        });
      }

      r = Math.random();

      if (r > 0.75) {
        filters.push({
          category: "ocr",
          value: _.sample(getters.popularOcr)
        });
      } else if (r > 0.6) {
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
      } else if (r > 0.4) {
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
      } else if (r > 0.1) {
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags),
          invert: true
        });
      } else if (r > 0.05) {
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags),
          invert: true
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags),
          invert: true
        });
      } else {
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags)
        });
        filters.push({
          category: "tag",
          value: _.sample(getters.popularTags),
          invert: true
        });
      }

      commit("setFilters", {
        filters,
        pushToURL: {
          filters: filters.map(
            f => f.category + "," + (f.invert ? "~" : "") + f.value
          )
        }
      });

      if (getters.numImagesFiltered === 0) {
        dispatch("setRandomFilter");
      }
    },

    addFilter({ state, commit }, { category, value, invert }) {
      let alreadyHasTag = false;
      state.filters.forEach(t => {
        if (
          t.category === category &&
          t.value === value &&
          t.invert === invert
        ) {
          alreadyHasTag = true;
        }
      });

      if (alreadyHasTag) return;
      let newFilters = state.filters;
      newFilters.push({ category, value, invert });

      commit("setFilters", {
        filters: newFilters,
        pushToURL: {
          filters: newFilters.map(
            f => f.category + "," + (f.invert ? "~" : "") + f.value
          )
        }
      });
    },

    replaceFilter({ state, commit }, { category, value, index, invert }) {
      let newFilters = [...state.filters];
      newFilters[index] = { category, value, invert };
      commit("setFilters", {
        filters: newFilters,
        pushToURL: {
          filters: newFilters.map(
            f => f.category + "," + (f.invert ? "~" : "") + f.value
          )
        }
      });
    },

    removeFilter({ state, commit }, index) {
      let newFilters = state.filters;
      newFilters.splice(index, 1);
      commit("setFilters", {
        filters: newFilters,
        pushToURL: {
          filters: newFilters.map(
            f => f.category + "," + (f.invert ? "~" : "") + f.value
          )
        }
      });
    }
  }
});
